import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Search, FileCheck, HandCoins, Lock, TrendingUp, ArrowRight, ShieldCheck, Scale, MapPin, ExternalLink,
} from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { PriceChart, Sparkline } from "@/components/CryptoChart";
import { useAuth } from "@/context/AuthContext";
import api, { fmtCurrency } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const HERO_IMG = "https://images.pexels.com/photos/16960662/pexels-photo-16960662.jpeg";
const GOLD_IMG = "https://images.pexels.com/photos/6764224/pexels-photo-6764224.jpeg";

const STEPS = [
  { n: "01", icon: Search, tk: "step.trace", xk: "step.traceText" },
  { n: "02", icon: FileCheck, tk: "step.freeze", xk: "step.freezeText" },
  { n: "03", icon: HandCoins, tk: "step.return", xk: "step.returnText" },
];

export default function Landing() {
  const { register } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", country: "United States", dial_code: "+1", password: "" });
  const [loading, setLoading] = useState(false);
  const [prices, setPrices] = useState([]);

  useEffect(() => {
    api.get("/crypto/prices").then(({ data }) => setPrices(data.coins)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register(form);
      toast.success("Case file created. Welcome aboard.");
      navigate(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      const { formatApiErrorDetail } = await import("@/lib/api");
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen">
      <div className="clu-ambient" />
      <div className="relative z-10">
        <SiteHeader />

        {/* HERO */}
        <section className="relative overflow-hidden border-b border-white/10">
          <div className="absolute inset-0 bg-cover bg-center opacity-25" style={{ backgroundImage: `url(${HERO_IMG})` }} />
          <div className="absolute inset-0 bg-gradient-to-b from-sapphire/70 via-sapphire/90 to-sapphire" />
          <div className="absolute inset-0 clu-grain opacity-20 pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-28 grid lg:grid-cols-5 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-3 space-y-8">
              <div className="inline-flex items-center gap-2 border border-gold/40 rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] font-mono text-gold">
                <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
                {t("hero.badge")}
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
                <span className="font-light text-white/60">{t("hero.title1")}</span>
                <br />
                <span className="font-extrabold">{t("hero.title2")}</span>{" "}
                <span className="font-extrabold text-gold">{t("hero.title3")}</span>
              </h1>
              <p className="text-sm font-mono uppercase tracking-[0.2em] text-gold">
                {t("hero.tagline")}
              </p>
              <p className="text-lg text-white/70 max-w-xl leading-relaxed">
                {t("hero.para")}
              </p>
              <a
                href="https://legalunion.uk/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-registry-link"
                className="inline-flex items-center gap-2 rounded-full border border-gold/50 px-5 py-2.5 text-sm font-semibold text-gold hover:bg-gold hover:text-sapphire transition-colors"
              >
                <Scale className="w-4 h-4" /> {t("hero.registry")} <ArrowRight className="w-4 h-4" />
              </a>
              <div className="flex flex-wrap gap-10 pt-2">
                {[["$47M+", t("stat.recovered")], ["2,140", t("stat.cases")], ["94%", t("stat.success")]].map(([v, l]) => (
                  <div key={l}>
                    <div className="font-display text-4xl font-extrabold tracking-tight text-gold">{v}</div>
                    <div className="text-xs uppercase tracking-[0.2em] text-white/50 font-mono mt-1">{l}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Signup card */}
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="lg:col-span-2">
              <div className="glass rounded-2xl p-8 relative shadow-[0_8px_32px_rgba(0,0,0,0.35)]">
                <div className="absolute -top-3 left-8 bg-gold text-sapphire text-xs px-2.5 py-1 rounded-full font-mono font-bold uppercase tracking-wider">{t("signup.badge")}</div>
                <h2 className="font-display text-3xl font-extrabold tracking-tight mb-1">{t("signup.title")}</h2>
                <p className="text-sm text-white/50 mb-6">{t("signup.sub")}</p>
                <form className="space-y-4" onSubmit={onSubmit}>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label={t("f.firstName")}><input data-testid="signup-first-name" required value={form.first_name} onChange={set("first_name")} className="clu-input" /></Field>
                    <Field label={t("f.lastName")}><input data-testid="signup-last-name" required value={form.last_name} onChange={set("last_name")} className="clu-input" /></Field>
                  </div>
                  <Field label={t("f.email")}><input data-testid="signup-email" required type="email" value={form.email} onChange={set("email")} className="clu-input" /></Field>
                  <div className="grid grid-cols-[110px_1fr] gap-2">
                    <Field label={t("f.code")}><input data-testid="signup-dial" value={form.dial_code} onChange={set("dial_code")} className="clu-input" /></Field>
                    <Field label={t("f.phone")}><input data-testid="signup-phone" required placeholder="555 000 0000" value={form.phone} onChange={set("phone")} className="clu-input" /></Field>
                  </div>
                  <Field label={t("f.country")}><input data-testid="signup-country" value={form.country} onChange={set("country")} className="clu-input" /></Field>
                  <Field label={t("f.password")}><input data-testid="signup-password" required type="password" placeholder={t("f.passwordPh")} value={form.password} onChange={set("password")} className="clu-input" /></Field>
                  <button type="submit" data-testid="signup-submit-btn" disabled={loading} className="w-full bg-gold hover:bg-gold-soft disabled:opacity-50 text-sapphire font-semibold py-3 rounded-md flex items-center justify-center gap-2 transition-colors">
                    {loading ? t("signup.creating") : t("signup.create")} <ArrowRight className="w-4 h-4" />
                  </button>
                  <p className="text-xs text-white/50 text-center pt-1">
                    {t("signup.already")} <Link data-testid="landing-login-link" to="/login" className="text-gold hover:underline">{t("signup.login")}</Link>
                  </p>
                </form>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Live market */}
        {prices.length > 0 && (
          <section className="border-b border-white/10 py-16">
            <div className="max-w-7xl mx-auto px-6">
              <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
                <div>
                  <p className="text-xs font-mono uppercase tracking-[0.2em] text-gold mb-2">{t("market.eyebrow")}</p>
                  <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{t("market.title")}</h2>
                </div>
                <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/50">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> {t("market.powered")}
                </span>
              </div>
              <div className="grid lg:grid-cols-3 gap-6">
                {(() => { const btc = prices.find((c) => c.symbol === "BTC"); return (
                  <div className="lg:col-span-2 glass rounded-2xl p-6" data-testid="landing-btc-chart">
                    <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                      <div className="flex items-center gap-3">
                        {btc && <img src={btc.image} alt="BTC" className="w-8 h-8" />}
                        <div>
                          <div className="font-display font-bold">Bitcoin</div>
                          <div className="text-xs font-mono text-white/40">BTC · 7-day</div>
                        </div>
                      </div>
                      {btc && (
                        <div className="text-right">
                          <div className="font-mono text-xl font-bold">{fmtCurrency(btc.price)}</div>
                          <div className={`font-mono text-sm ${btc.change_24h >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                            {btc.change_24h >= 0 ? "+" : ""}{btc.change_24h.toFixed(2)}%
                          </div>
                        </div>
                      )}
                    </div>
                    <PriceChart data={btc?.sparkline || []} color={btc && btc.change_24h < 0 ? "#EF4444" : "#D4AF37"} height={240} />
                  </div>
                ); })()}
                <div className="glass rounded-2xl p-6" data-testid="landing-market-list">
                  <p className="text-xs font-mono uppercase tracking-[0.2em] text-white/50 mb-4">{t("market.top")}</p>
                  <div className="space-y-1">
                    {prices.slice(0, 6).map((c) => (
                      <div key={c.id} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
                        <img src={c.image} alt={c.symbol} className="w-6 h-6" />
                        <span className="font-mono text-sm w-12">{c.symbol}</span>
                        <div className="flex-1 max-w-[70px]"><Sparkline data={c.sparkline} color={c.change_24h >= 0 ? "#10B981" : "#EF4444"} height={26} /></div>
                        <span className="font-mono text-sm ml-auto">{fmtCurrency(c.price)}</span>
                        <span className={`font-mono text-xs w-14 text-right ${c.change_24h >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                          {c.change_24h >= 0 ? "+" : ""}{c.change_24h.toFixed(1)}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* PROCESS */}
        <section className="border-b border-white/10 py-24">
          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-12 items-start">
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-gold mb-3">{t("process.eyebrow")}</p>
              <h2 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight mb-4">{t("process.title")}</h2>
              <p className="text-white/60">{t("process.sub")}</p>
            </div>
            <div className="lg:col-span-2 grid sm:grid-cols-3 gap-6">
              {STEPS.map((s, i) => (
                <motion.div key={s.n} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  className="glass rounded-2xl p-6 hover:border-gold/40 hover:-translate-y-1 transition-all">
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-mono text-xs text-white/40 tracking-widest">{s.n}</span>
                    <s.icon className="w-6 h-6 text-gold" />
                  </div>
                  <h3 className="font-display text-2xl font-extrabold tracking-tight mb-2">{t(s.tk)}</h3>
                  <p className="text-sm text-white/60 leading-relaxed">{t(s.xk)}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* WHO WE ARE + GET IN TOUCH */}
        <section className="border-b border-white/10 py-24" data-testid="about-contact-section">
          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-6">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-gold">{t("about.eyebrow")}</p>
              <h2 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight">{t("about.title")}</h2>
              <p className="text-white/60 leading-relaxed">
                {t("about.p1")}
              </p>
              <div className="glass rounded-2xl p-6">
                <p className="font-display text-lg font-bold mb-2">{t("about.principlesTitle")}</p>
                <p className="text-sm text-white/60 leading-relaxed">
                  {t("about.principlesText")}
                </p>
              </div>
              <a href="https://legalunion.uk/registry/" target="_blank" rel="noopener noreferrer" data-testid="about-registry-link"
                className="inline-flex items-center gap-2 text-sm font-semibold text-gold hover:underline">
                <Scale className="w-4 h-4" /> {t("about.viewRegistry")} <ArrowRight className="w-4 h-4" />
              </a>
              <div className="glass rounded-2xl p-6" data-testid="fca-regulatory-block">
                <p className="font-display text-lg font-bold mb-2">{t("about.fcaTitle")}</p>
                <p className="text-sm text-white/60 leading-relaxed mb-4">
                  {t("about.fcaText")}
                </p>
                <a href="https://www.fca.org.uk/firms/cryptoassets-information" target="_blank" rel="noopener noreferrer" data-testid="about-fca-link"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-gold hover:underline">
                  <ExternalLink className="w-4 h-4" /> {t("about.fcaLink")} <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>

            <div className="glass rounded-2xl p-8" data-testid="contact-panel">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-gold mb-2">{t("contact.eyebrow")}</p>
              <h3 className="font-display text-3xl font-extrabold tracking-tight mb-6">{t("contact.title")}</h3>
              <div className="flex items-start gap-3 mb-5">
                <MapPin className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-mono uppercase tracking-[0.2em] text-white/50 mb-1">{t("contact.addressLabel")}</p>
                  <p className="text-white/80 leading-relaxed" data-testid="contact-address">
                    50 Bank Street, Canary Wharf<br />London E14 5NT<br />United Kingdom
                  </p>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden border border-white/10">
                <iframe
                  title="Crypto-LegalUnion office map"
                  data-testid="contact-map"
                  src="https://www.google.com/maps?q=50+Bank+Street+Canary+Wharf+London+E14+5NT+United+Kingdom&output=embed"
                  width="100%"
                  height="280"
                  style={{ border: 0, filter: "grayscale(0.2) invert(0.9) hue-rotate(180deg)" }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </section>

        {/* TRUST */}
        <section className="border-b border-white/10 py-24">
          <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-6">
            {[
              { icon: Lock, title: t("trust.custodyTitle"), text: t("trust.custodyText"), img: GOLD_IMG },
              { icon: TrendingUp, title: t("trust.dashTitle"), text: t("trust.dashText"), img: null },
            ].map((c) => (
              <div key={c.title} className="glass rounded-2xl p-8 hover:border-white/25 transition-colors relative overflow-hidden">
                {c.img && <div className="absolute inset-0 bg-cover bg-center opacity-10" style={{ backgroundImage: `url(${c.img})` }} />}
                <div className="relative">
                  <div className="text-gold mb-4"><c.icon className="w-6 h-6" /></div>
                  <h3 className="font-display text-xl font-bold tracking-tight mb-2">{c.title}</h3>
                  <p className="text-sm text-white/60 leading-relaxed">{c.text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <footer className="py-10">
          <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row justify-between gap-4 items-center text-xs text-white/40 font-mono uppercase tracking-[0.2em]">
            <span className="flex items-center gap-2"><Scale className="w-4 h-4 text-gold/70" /> {t("footer.copyright")}</span>
            <span>{t("footer.compliance")}</span>
          </div>
        </footer>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.15em] text-white/60 font-mono block mb-2">{label}</span>
      {children}
    </label>
  );
}
